import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { keyframes } from 'tss-react';
import { useRouter } from 'next/router';
import palette from '@ui/palette';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import { IconType } from '@ui/Icon/types';
import type { BreadcrumbItem } from '../../BaseLayout/components/Breadcrumbs';
import Breadcrumb from '../../BaseLayout/components/Breadcrumbs/Breadcrumb';
import { routeIcons } from '../../BaseLayout/components/Navigation/routes';
import { useStudioRoutesQuery } from '../../BaseLayout/components/Navigation/operation.generated';
import FoldersNavBreadcrumb from '../../Folders/Navigation';
import { DriveItemType } from '../../Drives/types';
import { alphaWhite } from '../../../util/cssUtil';
import useStudioBreadcrumbTrail, {
  StudioBreadcrumbProject,
  StudioBreadcrumbTrailItem,
  studioProjectHref,
} from './useStudioBreadcrumbTrail';

/**
 * The line box of a resolved crumb's label. Every crumb the container renders is
 * a `pMd` `Typography`, whose line height is 18px, so the placeholder's bar is
 * that tall and the row it sits in resolves to the same height a real crumb
 * does. Taken from the crumb rather than chosen, because R10 forbids the trail
 * shifting anything when it lands.
 */
const CRUMB_LABEL_HEIGHT = 18;

/**
 * Nominal width for the placeholder bar. Width is the one dimension that cannot
 * shift a resolved crumb: the trail is the last thing in the row and grows
 * rightwards, so the crumbs land at the placeholder's leading edge whatever this
 * is. It is sized to read as one mid-length crumb, under the 200px the crumb and
 * the container both cap their labels at.
 */
const PLACEHOLDER_BAR_WIDTH = 112;

const placeholderPulse = keyframes({
  '0%': { opacity: 1 },
  '50%': { opacity: 0.4 },
  '100%': { opacity: 1 },
});

const useStyles = makeStyles()(theme => ({
  root: {
    height: '100%',
    margin: theme.spacing(0, 1.5),
    minWidth: 0,
  },
  list: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    height: '100%',
    overflow: 'hidden',
    maxWidth: '100%',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  separator: {
    color: palette.gray_75,
  },
  trail: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  /**
   * Mirrors the container's own crumb section - the same `spacing(1.5)` gap
   * between separator and crumb, the same `gray_75` the separator icon inherits
   * - so the chevron sits exactly where the container will put it.
   */
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    color: palette.gray_75,
  },
  /**
   * Stands in for the crumb's button box: the same `spacing(0.75, 1)` padding
   * the crumb carries, so this resolves to the crumb's height without naming a
   * height of its own, and the bar lands where the crumb's label will.
   */
  placeholderCrumb: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.75, 1),
  },
  placeholderBar: {
    height: CRUMB_LABEL_HEIGHT,
    width: PLACEHOLDER_BAR_WIDTH,
    borderRadius: theme.spacing(0.5),
    backgroundColor: alphaWhite(0.09),
    animation: `${placeholderPulse} 2s linear infinite`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
}));

/**
 * `Studio` never navigates - `Breadcrumb` defaults `disableLink` to true, so the
 * route below is only ever used as a key and never rendered as a link. The label
 * and icon match the `Studio` entry the studio routes resolver always returns.
 */
const STUDIO_CRUMB: BreadcrumbItem = {
  label: 'Studio',
  route: '/dashboard/studio',
  iconType: routeIcons.Studio,
};

const PROJECTS_LABEL = 'Projects';

/**
 * The container hands the item's `type` back through `onNavigate`, which this
 * trail ignores, but the prop is required. The project crumb is the one whose
 * href is the page we are already on; anything ahead of the folders is the
 * workspace.
 */
const driveItemTypeFor = (
  item: StudioBreadcrumbTrailItem,
  index: number,
  currentHref: string
) => {
  if (item.href === currentHref) return DriveItemType.Project;
  return index === 0 ? DriveItemType.Drive : DriveItemType.Folder;
};

export interface StudioBreadcrumbsTrailProps {
  /**
   * Ordered workspace -> folders -> project crumbs, as resolved by
   * `useStudioBreadcrumbTrail`. Empty renders `Studio` / `Projects` alone.
   */
  items: StudioBreadcrumbTrailItem[];
  /**
   * The open project's own route. `FoldersNavBreadcrumb` marks a child active -
   * and therefore renders it disabled instead of as an enabled button - only
   * when the child's href includes this value, so the trailing project crumb
   * stays non-interactive.
   */
  currentHref: string;
  /**
   * True while `useStudioBreadcrumbTrail` is still resolving. Holds the space
   * after `Projects` with a placeholder instead of rendering the part of the
   * trail that is known, per R10. A degraded trail is not loading - it resolves
   * to the project name alone and renders through the normal path.
   */
  loading?: boolean;
}

/**
 * Renders the Studio breadcrumb trail from already-resolved items.
 *
 * `Studio` and `Projects` are rendered outside the collapsing container: the
 * container's item renders a bare label with no icon slot, and its separator is
 * a single container-level prop, so neither the icons nor the slash-then-chevron
 * separator pattern can come from it. Everything after `Projects` goes through
 * the container with `hideRoot` set, so every trail item counts toward its
 * two-visible budget and earlier crumbs collapse into its dropdown.
 *
 * While `loading`, the space after `Projects` is held by a placeholder rather
 * than by however much of the trail happens to be known: the trail is rendered
 * whole or not at all (R10). Nothing here gates the page - the Studio editor
 * paints on its own schedule and this header fills in after it.
 */
export const StudioBreadcrumbsTrail = ({
  items,
  currentHref,
  loading = false,
}: StudioBreadcrumbsTrailProps) => {
  const { classes } = useStyles();
  const router = useRouter();
  const { data: routesData } = useStudioRoutesQuery();

  /**
   * The resolver returns either `/dashboard/projects` or `/dashboard/workspaces`
   * under the `Projects` label depending on the user's workspace ability, so the
   * destination is read from the query rather than hardcoded. Until it resolves
   * the crumb renders as a plain label - better than linking to a list the user
   * may not be entitled to.
   */
  const projectsRoute = routesData?.studioRoutes.find(
    route => route.label === PROJECTS_LABEL
  );

  const projectsCrumb: BreadcrumbItem = {
    label: PROJECTS_LABEL,
    route: projectsRoute?.route || '',
    iconType: routeIcons.Projects,
  };

  return (
    <Box
      component="nav"
      aria-label="breadcrumb"
      aria-busy={loading || undefined}
      className={classes.root}
    >
      <Box component="ol" className={classes.list}>
        {/**
         * `Studio` and `Projects` render immediately in every state: they come
         * from the studio routes query the header already runs, so they are
         * interactive while the trail is still resolving.
         */}
        <Breadcrumb route={STUDIO_CRUMB} />
        {/* Decorative: the slash R7 puts between `Studio` and `Projects`. */}
        <Box component="li" aria-hidden className={classes.separator}>
          <Typography variant="pMd">/</Typography>
        </Box>
        <Breadcrumb route={projectsCrumb} disableLink={!projectsRoute} />
        {loading && (
          /**
           * The placeholder carries the chevron the container would render
           * ahead of its first crumb, so the separator is present in both
           * states and the crumbs land where the bar was rather than being
           * pushed right by a chevron appearing with them. `aria-hidden`
           * because there is no crumb here to announce - `aria-busy` on the
           * `nav` says the trail is still coming.
           */
          <Box component="li" aria-hidden className={classes.trail}>
            <Box className={classes.placeholder}>
              <Icon type={IconType.chevronRight} color="inherit" />
              <Box className={classes.placeholderCrumb}>
                <Box className={classes.placeholderBar} />
              </Box>
            </Box>
          </Box>
        )}
        {!loading && items.length > 0 && (
          /**
           * The container owns every crumb after `Projects` and exposes no
           * per-crumb hook, so there is nowhere to hang `aria-current="page"` on
           * the trailing project crumb alone. Marking this list item would claim
           * the workspace and every folder as the current page too, which is
           * worse than claiming nothing, so the marker is omitted until the
           * container can carry it per-crumb. The chevron separating `Projects`
           * from the first of these crumbs comes from the container.
           */
          <Box component="li" className={classes.trail}>
            <FoldersNavBreadcrumb.Container
              root=""
              hideRoot
              currentHref={currentHref}
              separatorIcon={IconType.chevronRight}
              onNavigate={(route: string) => {
                // `push`, not `replace`: Back has to return to the Studio page.
                if (route) router?.push(route);
              }}
            >
              {items.map((item, index) => (
                <FoldersNavBreadcrumb.Item
                  key={item.id}
                  id={item.id}
                  itemLabel={item.itemLabel}
                  href={item.href}
                  type={driveItemTypeFor(item, index, currentHref)}
                />
              ))}
            </FoldersNavBreadcrumb.Container>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export interface StudioBreadcrumbsProps {
  project: StudioBreadcrumbProject;
}

/**
 * The Studio header's breadcrumb trail: where the open project sits in the
 * workspace and folder hierarchy, and a way back to any level of it.
 */
const StudioBreadcrumbs = ({ project }: StudioBreadcrumbsProps) => {
  /**
   * `isDegraded` is deliberately not read here: a degraded trail is already one
   * item - the project name - so it renders through the same path as any other
   * resolved trail. What matters for R10 is only that `loading` is false by
   * then, so the placeholder gives way rather than sticking around.
   */
  const { items, loading } = useStudioBreadcrumbTrail({ project });

  return (
    <StudioBreadcrumbsTrail
      items={items}
      loading={loading}
      currentHref={studioProjectHref(project.id)}
    />
  );
};

export default StudioBreadcrumbs;
